import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { Field, Form, FormikBag, FormikProps, withFormik } from 'formik';
import { History, Location } from 'history';
import { withRouter } from 'react-router-dom';
import AlertError from '../../components/AlertError';
import { formatMobileNumber } from '../../lib/formatter';
import FormikPhoneNumberField from '../formik/FormikPhoneNumberField';
import FormLabelAndInput from '../FormLabelAndInput';
import { formSchema } from './validations';

const useStyles: any = makeStyles((theme: any) => ({
  generateAuthFormContainer: {
    padding: theme.spacing(2, 2.5),
  },
  inputBase: {
    margin: theme.spacing(1, 0, 0),
    background: theme.palette.common.white,
    '& .MuiOutlinedInput-input': {
      paddingTop: '13px',
      paddingBottom: '13px',
    },
  },
  notchedOutline: {
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
    },
  },
  messageContainer: {
    marginBottom: theme.spacing(1.5),
  },
}));

type FormValues = {
  mobileNumber: string;
};

type LoginFormProps = FormikProps<FormValues> & {
  mobileNumber: string;
  values: FormValues;
  isSubmitting: boolean;
  stateError: string;
  history: History<Location>;
  setError: (error?: string) => void;
  setLoginStep: (step: number) => void;
  getPaymentsCode: (mobileNumber: string) => Promise<any>;
};

const GenerateAuthForm = (props: LoginFormProps) => {
  const { isSubmitting, values, errors, handleSubmit, stateError } = props;
  const classes = useStyles();
  const validationError = errors && !!errors.mobileNumber;

  const formatAuPhone = (value: string): string => {
    if (value) {
      let groupOne;
      let groupTwo;
      let groupThree;
      let groupOneLength = 3;
      if (value.startsWith('0')) {
        groupOneLength = 4;
      }
      if (value.length > 0) {
        groupOne = value.substring(0, groupOneLength);
      }
      if (value.length > groupOneLength) {
        groupTwo = value.substring(groupOneLength, groupOneLength + 3);
      }
      if (value.length > groupOneLength + 3) {
        groupThree = value.substring(groupOneLength + 3, groupOneLength + 6);
      }
      return `${groupOne}${groupTwo ? ' ' + groupTwo : ''}${groupThree ? ' ' + groupThree : ''}`;
    }
    return value;
  };

  return (
    <Grid className={classes.generateAuthFormContainer} container>
      <Grid item xs={12}>
        <Form data-testid='uia-generateAuthForm' noValidate autoComplete='off' onSubmit={handleSubmit}>
          <Grid container spacing={1}>
            {stateError && (
              <Grid item xs={12} className={classes.messageContainer}>
                <AlertError message={stateError} data-testid='uia-submit-error' />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormLabelAndInput label='What is your mobile number?'>
                <Field
                  component={FormikPhoneNumberField}
                  variant='outlined'
                  id='mobileNumber'
                  name='mobileNumber'
                  placeholder='Enter mobile number'
                  fullWidth
                  pattern='^0?5[0-9]{2} [0-9]{3} [0-9]{3}'
                  format={formatAuPhone}
                  InputProps={{
                    classes: {
                      root: classes.inputBase,
                      notchedOutline: classes.notchedOutline,
                    },
                    autoFocus: true,
                    disabled: isSubmitting,
                  }}
                  data-testid='uia-mobile-number'
                  value={values.mobileNumber}
                  formHelperTextClassName={classes.formHelperTextBase}
                />
              </FormLabelAndInput>
            </Grid>
            <Grid item xs={12}>
              <Button
                size='large'
                variant='contained'
                color='primary'
                type='submit'
                fullWidth
                disabled={!!validationError || isSubmitting}
                data-testid='uia-submit-button'
              >
                Send verification code via SMS
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Grid>
    </Grid>
  );
};

export default withRouter(
  withFormik({
    mapPropsToValues: () => {
      return {
        mobileNumber: '',
      };
    },
    validationSchema: formSchema,
    handleSubmit: (values: any, formikBag: FormikBag<any, any>) => {
      const { props } = formikBag;
      formikBag.setSubmitting(true);
      const mobileNumber = formatMobileNumber(values.mobileNumber);

      props.getPaymentsCode(props.checkOutId, mobileNumber).then((response: any) => {
        formikBag.setSubmitting(false);
        const { payload } = response;
        if (payload.response && payload.response.status === 400 && payload.response && payload.response.data) {
          const { message } = payload.response.data;
          props.setError(message);
        } else {
          props.setError(null);
        }
      });
    },
  })(GenerateAuthForm)
);
